import { getIsLocalDev } from "@pathwright/utils"
import { isUserProxied } from "@pathwright/utils/src/token/utils"
import { getAdminEnv } from "@pathwright/utils/src/url/admin"
import getPWAdminSSOUrl from "@pathwright/auth/src/views/auth/utils/getPWAdminSSOUrl"
import { capitalCase } from "change-case"
import { useMemo } from "react"
import { useHistory } from "react-router-dom"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { PWAdminCmdKOptionWithKeyControl } from "./types"
import useIsPWAdminUtilEnabled from "./useIsPWAdminUtilEnabled"

type PWAdminSSOLinksHookProps = {
  // Scope the urls to either the curent user (whether proxied or not),
  // or to the proxying user (simply behaves as "current" when no user is not proxying).
  userScope: "current" | "proxier"
}

// With PW Admin SSO links, we can now generate direct links to a Pathwright
// platform URL with the target user pre-filled meaning we can more easily
// share links on the Pathwright team for debugging purposes.
export function usePWAdminSSOLinks(options?: PWAdminSSOLinksHookProps) {
  const { userScope } = (options ||= { userScope: "current" })

  // Need to regenerate link when location changes.
  const history = useHistory()
  const pwContext = usePathwrightContext()
  const enablePWAdminSSOLinks = useIsPWAdminUtilEnabled()

  function getSSOUrl() {
    const isProxied = isUserProxied()
    const targetUser =
      isProxied && userScope === "current" ? pwContext.me?.email : ""
    const ssoUrl = getPWAdminSSOUrl(`admin::${targetUser}`)
    return ssoUrl
  }

  // Generates a url string based on the stored school config url and sso redirect_url.
  // This can be passed as the ?school query param in localhost urls.
  function constructSchoolConfigUrlRedirect() {
    const ssoUrl = new URL(getSSOUrl())
    const redirectUrl = new URL(ssoUrl.searchParams.get("redirect_url")!)
    const schoolUrl = localStorage.getItem("school_config_url") || ""
    const localDevRedirectUrl = new URL(
      redirectUrl.pathname + redirectUrl.search,
      schoolUrl
    )
    return localDevRedirectUrl.toString()
  }

  // Generates the SSO url and updates the redirect_url query param using
  // the provided redirectUrl (str or fn).
  function getRedirectedSSOUrl(
    redirectUrl: string | ((redirectUrl: string) => string)
  ) {
    const ssoUrl = new URL(getSSOUrl())
    const currentRedirectUrl = ssoUrl.searchParams.get("redirect_url")!
    const nextRedirectUrl =
      typeof redirectUrl === "function"
        ? redirectUrl(currentRedirectUrl)
        : redirectUrl
    ssoUrl.searchParams.set("redirect_url", nextRedirectUrl)
    return ssoUrl.toString()
  }

  return useMemo(() => {
    if (enablePWAdminSSOLinks) {
      // When in local dev, redirect to the school url, otherwise, redirect to local dev url.
      if (getIsLocalDev()) {
        return {
          // Replace the redirect_url with the localhost url, using the
          // school config url as the ?school query param.
          currentUrl: getRedirectedSSOUrl(
            `http://localhost:3000?school=${constructSchoolConfigUrlRedirect()}`
          ),

          // Replace the redirect_url origin with the school config url.
          reverseEnvUrl: getRedirectedSSOUrl(constructSchoolConfigUrlRedirect())
        }
      } else {
        return {
          // Just the SSO url.
          currentUrl: getSSOUrl(),
          // Replace the redirect_url with the localhost url, using the
          // current redirect_url as the ?school query param.
          reverseEnvUrl: getRedirectedSSOUrl(
            (redirectUrl) => `http://localhost:3000?school=${redirectUrl}`
          )
        }
      }
    } else {
      return {
        currentUrl: "",
        reverseEnvUrl: ""
      }
    }
  }, [enablePWAdminSSOLinks, pwContext.me?.id, history.location.key])
}

export function usePWAdminSSOOptions(): PWAdminCmdKOptionWithKeyControl[] {
  const { currentUrl, reverseEnvUrl } = usePWAdminSSOLinks()

  return [
    {
      label: "Current Auth Link",
      icon: "external-link",
      keyControl: "Meta+Shift+.",
      href: currentUrl,
      copy: currentUrl
    },
    {
      label: `${
        getIsLocalDev() ? capitalCase(getAdminEnv()) : "Local Dev"
      } Auth Link`,
      icon: "external-link",
      keyControl: "Meta+Shift+,",
      href: reverseEnvUrl,
      copy: reverseEnvUrl
    }
  ]
}
